import { useCallback, useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import { flattenMidiNotes } from '../utils/midiJson';
import { getCalloutTime } from '../utils/chartRun';
import { EXPONENTIAL } from '../utils/chordViolin';
import {
  createMaster,
  createVoice,
  curveVelocity,
  getRingTime,
  loadTone,
  MIN_NOTE_DURATION,
  RELEASE_TAIL,
  resumeContext,
  scheduleMetronomeClick,
} from '../utils/toneVoices';
import { createDrumKit } from '../utils/drumKit';
import { connectSends, createSendBuses, disposeSends, rampSends } from '../utils/effectSends';
import { getRunDrumHits } from '../utils/drumGrooves';
import { getBeatsPerBar } from '../utils/songTiming';
import { cancelSpeech, primeSpeech, speak } from '../utils/speech';
import { getInstrumentName } from '../utils/midiInstruments';
import i18n from '../i18n';

/**
 * Plays a chord-bar grid back, with the metronome, so a chart can be checked by ear.
 *
 * Close kin to useMidiPlayer — same voices, same master limiter, same lazy Tone fetch, and the
 * teardown is the same discipline. What it does not share is the reason it exists: the click and
 * the chords have to be one performance. The metronome engine everywhere else in the app runs its
 * own AudioContext, and two contexts started a few milliseconds apart give a click whose phase
 * against the chords is whatever the browser happened to do that time — which is useless for the
 * one question this player answers, namely whether the chord changes land on the bar lines.
 * Scheduling both onto Tone's transport makes that exact by construction.
 *
 * Kept separate from useMidiPlayer rather than bolted onto it: that hook plays a clip someone
 * recorded, at a tempo and key they can nudge, and it has no notion of bars at all. This one plays
 * a grid the user is editing, loops it, counts it in, and reports which bar is sounding. Merging
 * them would mean one hook with two disjoint halves.
 *
 * @param {object} run - From buildChartRun: the chart, or several parts' charts end to end, as one
 *   clip laid out in segments. Each note names the instrument it is played with.
 * @param {boolean} isActive - Whether the modal is open; Tone is fetched at that point
 * @param {object} options
 * @param {string} options.timeSignature - Beats per bar, read the same way the metronome reads it
 * @param {boolean} options.metronome - Whether to click at all. Heard live, like the levels
 * @param {number} options.metronomeVolume - How loud, 0..1; null follows the metronome setting
 * @param {number} options.instrumentVolume - Level for the chords themselves, 0..1 of the preset's
 *   own tuned level
 * @param {boolean} options.loop - Repeat the grid until stopped. Only a run of one segment loops.
 * @param {boolean} options.countIn - One bar of clicks before the first chord. Read on Play. A run
 *   whose first part comes in during the count-in (`run.countInEntry`) has that part's first bar
 *   played over those clicks rather than after them, when it is played from the top.
 * @param {number} options.startBar - Bar of the grid to begin at. The count-in still plays first,
 *   and a looping grid still wraps to its own top rather than back to this bar.
 * @param {object} options.drumGroove - One of DRUM_GROOVES to play under the chords, or null for none.
 *   A segment's own `drumGroove`, where it has one, plays over it.
 * @param {number} options.drumVolume - Level for the drums, 0..1 of the kit's own balance
 * @param {number} options.melodyVolume - Level for the melody, 0..1 of its preset's own
 * @param {number} options.bassVolume - Level for the bass player, 0..1 of its preset's own
 * @param {number} options.padVolume - Level for the pad, 0..1 of its presets' own
 * @param {object} options.bandVolumes - Level for each of the band's extra players, by BAND_LAYERS id, 0..1 of
 *   its preset's own. Kept the same object while the levels are, since a new one ramps every layer.
 * @param {object} options.effects - The song's chartEffects: how much each player sends to the reverb
 *   and the delay. Heard live, and kept the same object while it is, like bandVolumes.
 * @param {number} options.chordsUnderMelody - Share of instrumentVolume the chords keep through a
 *   part whose melody plays, so the tune sits on top
 * @param {string[]} options.callouts - What to say a bar before each segment comes in, one per
 *   segment (null where nothing is said), or null for none. The first is said over the count-in.
 * @param {boolean} options.speakCallouts - Whether to say them. Heard live, like the click
 * @param {number} options.calloutVolume - How loud, 0..1. Read as each is said, like the click's level
 * @param {string} options.calloutLang - The language they're written in, 'en' or 'es'
 * @returns {{ isLoading: boolean, isPlaying: boolean, error: string, play: function,
 *   stop: function, activeNotes: Set<number>, currentSegment: number, currentBar: number,
 *   currentChord: number, countInBeat: number, beat: number, getSpectrum: function, chordsCut: number }}
 *   `currentSegment` is the run's segment sounding right now, and `currentBar` and `currentChord`
 *   the bar and chord within it, all -1 during the count-in and while stopped. `countInBeat` is the other half of that: which click of
 *   the count-in is sounding, 1..beatsPerBar, and 0 from the first chord onwards — or, where the chart
 *   comes in during the count-in, through its first bar with the bar and chord live beside it. `beat` is the
 *   beat of the bar sounding, 1..beatsPerBar, through the count-in and the chart alike — 0 while
 *   stopped and once the chart has played through.
 */

// Shared so an idle player hands out the same object on every render rather than a fresh Set
const NO_ACTIVE_NOTES = new Set();
const NO_VOLUMES = {};
const NO_EFFECTS = {};
const NO_SEGMENT = -1;
const NO_BAR = -1;
const NO_CHORD = -1;
const NO_BEAT = 0;

// The strike ringing at a moment: the last one already struck. A search rather than a pointer that
// only moves forward, because a looping grid wraps the position back to its top. Finds the segment
// playing the same way, from the seconds each starts at.
const strikeAt = (times, position) => {
  let found = NO_CHORD;
  let low = 0;
  let high = times.length - 1;

  while (low <= high) {
    const mid = (low + high) >> 1;

    if (times[mid] <= position) {
      found = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return found;
};

const haveSameNotes = (a, b) => {
  if (a.size !== b.size) return false;

  for (const midi of a) {
    if (!b.has(midi)) return false;
  }

  return true;
};

/** The notes sounding at a point in the grid — see useMidiPlayer for why this reads the clock. */
const getSoundingNotes = (notes, seconds) => {
  const sounding = new Set();

  for (const note of notes) {
    if (note.time > seconds) break;
    if (note.time + Math.max(note.duration, MIN_NOTE_DURATION) > seconds) sounding.add(note.midi);
  }

  return sounding;
};

// A voice per instrument the notes name, into the same gain. A run crosses from one part to the next,
// and each part plays its own instrument.
const createVoices = async (Tone, notes, destination) => {
  const ids = [...new Set(notes.map((note) => note.instrumentId))];
  const voices = await Promise.all(ids.map((id) => createVoice(Tone, id, destination)));

  return new Map(ids.map((id, i) => [id, voices[i]]));
};

// Voice then effects, in signal order
const disposeVoice = ({ voice, effects }) => {
  voice.releaseAll();
  voice.dispose();
  effects?.forEach((node) => node.dispose());
};

const useChordGridPlayer = (
  run,
  isActive = false,
  {
    timeSignature = '4/4',
    metronome = true,
    metronomeVolume = null,
    instrumentVolume = 1,
    loop = false,
    countIn = true,
    startBar = 0,
    drumGroove = null,
    drumVolume = 1,
    melodyVolume = 1,
    bassVolume = 1,
    padVolume = 1,
    bandVolumes = NO_VOLUMES,
    effects = NO_EFFECTS,
    chordsUnderMelody = 1,
    callouts = null,
    speakCallouts = false,
    calloutVolume = 1,
    calloutLang = 'en',
  } = {},
) => {
  const toneRef = useRef(null);
  const voicesRef = useRef([]);
  const gainRef = useRef(null);
  const drumKitRef = useRef(null);
  const drumGainRef = useRef(null);
  const melodyRef = useRef(null);
  const bassRef = useRef(null);
  const padRef = useRef(null);
  // The band's extra players, by id, each `{ voices, gain }` like the refs above
  const bandRef = useRef({});
  const masterRef = useRef(null);
  // Listens to the master for a view to draw how loud the band is; the clicks bypass it
  const analyserRef = useRef(null);
  // The reverb and delay buses, and each player's sends into them by SEND_PLAYERS id
  const busesRef = useRef(null);
  const sendsRef = useRef({});
  const partsRef = useRef([]);
  const endTimeoutRef = useRef(null);
  const frameRef = useRef(null);
  // Bumped by every teardown, so a play still loading when it's stopped, or played again, goes no further
  const playIdRef = useRef(0);

  // Read when each click is scheduled rather than closed over, so the slider is heard on the
  // clicks still to come instead of tearing the performance down and starting it again
  const volumeRef = useRef(metronomeVolume);
  volumeRef.current = metronomeVolume;

  // The same for switching it: the clicks are always scheduled, and each one asks whether to sound
  const metronomeRef = useRef(metronome);
  metronomeRef.current = metronome;

  // And the same for the callouts: always scheduled when there are some, each one asks whether to speak
  const speakCalloutsRef = useRef(speakCallouts);
  speakCalloutsRef.current = speakCallouts;
  const calloutVolumeRef = useRef(calloutVolume);
  calloutVolumeRef.current = calloutVolume;

  const [currentSegment, setCurrentSegment] = useState(NO_SEGMENT);

  // The chords step back through a part whose melody plays, and come back up in one that has none.
  // The part is the one sounding, or during the count-in and on play the first, which is next.
  const isUnderMelody = Boolean(run?.segments[Math.max(currentSegment, 0)]?.hasMelody);
  const chordsLevel = isUnderMelody ? instrumentVolume * chordsUnderMelody : instrumentVolume;

  // The chords' level is a live node, so the same drag moves what is already sounding
  const instrumentVolumeRef = useRef(chordsLevel);
  instrumentVolumeRef.current = chordsLevel;

  const drumVolumeRef = useRef(drumVolume);
  drumVolumeRef.current = drumVolume;

  const melodyVolumeRef = useRef(melodyVolume);
  melodyVolumeRef.current = melodyVolume;

  const bassVolumeRef = useRef(bassVolume);
  bassVolumeRef.current = bassVolume;

  const padVolumeRef = useRef(padVolume);
  padVolumeRef.current = padVolume;

  const bandVolumesRef = useRef(bandVolumes);
  bandVolumesRef.current = bandVolumes;

  const effectsRef = useRef(effects);
  effectsRef.current = effects;

  // Read when play is pressed rather than closed over. The cursor it follows moves while the chart
  // is playing, and a `play` whose identity changed with it would put startBar in the dependency
  // lists below — where it would tear the performance down on every bar the cursor crossed.
  const startBarRef = useRef(startBar);
  startBarRef.current = startBar;

  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState('');
  const [activeNotes, setActiveNotes] = useState(NO_ACTIVE_NOTES);
  const [currentBar, setCurrentBar] = useState(NO_BAR);
  const [currentChord, setCurrentChord] = useState(NO_CHORD);
  const [countInBeat, setCountInBeat] = useState(NO_BEAT);
  const [beat, setBeat] = useState(NO_BEAT);

  const beatsPerBar = getBeatsPerBar(timeSignature);

  const teardown = useCallback(() => {
    playIdRef.current += 1;

    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }

    setActiveNotes(NO_ACTIVE_NOTES);
    setCurrentSegment(NO_SEGMENT);
    setCurrentBar(NO_BAR);
    setCurrentChord(NO_CHORD);
    setCountInBeat(NO_BEAT);
    setBeat(NO_BEAT);

    if (endTimeoutRef.current) {
      clearTimeout(endTimeoutRef.current);
      endTimeoutRef.current = null;
    }

    partsRef.current.forEach((part) => {
      part.stop();
      part.dispose();
    });
    partsRef.current = [];

    cancelSpeech();

    if (toneRef.current) {
      const transport = toneRef.current.getTransport();

      transport.stop();
      transport.cancel();
    }

    voicesRef.current.forEach(disposeVoice);
    voicesRef.current = [];

    if (gainRef.current) {
      gainRef.current.dispose();
      gainRef.current = null;
    }

    // Voices, then gain, in signal order like the chords' own chain
    [melodyRef, bassRef, padRef].forEach((ref) => {
      if (!ref.current) return;

      const { voices, gain, shaping } = ref.current;

      voices.forEach(disposeVoice);
      gain.dispose();
      if (shaping) shaping.dispose();
      ref.current = null;
    });

    Object.values(bandRef.current).forEach(({ voices, gain }) => {
      voices.forEach(disposeVoice);
      gain.dispose();
    });
    bandRef.current = {};

    if (drumKitRef.current) {
      drumKitRef.current.dispose();
      drumKitRef.current = null;
    }

    if (drumGainRef.current) {
      drumGainRef.current.dispose();
      drumGainRef.current = null;
    }

    // Sends, then the buses they feed, before the master they play into
    Object.values(sendsRef.current).forEach(disposeSends);
    sendsRef.current = {};

    if (busesRef.current) {
      busesRef.current.nodes.forEach((node) => node.dispose());
      busesRef.current = null;
    }

    if (analyserRef.current) {
      analyserRef.current.dispose();
      analyserRef.current = null;
    }

    if (masterRef.current) {
      masterRef.current.dispose();
      masterRef.current = null;
    }
  }, []);

  // Fetch Tone while the user is looking at the modal rather than when they hit play
  useEffect(() => {
    if (!isActive || toneRef.current) return;

    let cancelled = false;

    loadTone().then(
      (Tone) => {
        if (!cancelled) toneRef.current = Tone;
      },
      () => {},
    );

    return () => {
      cancelled = true;
    };
  }, [isActive]);

  useEffect(() => {
    return () => teardown();
  }, [teardown]);

  // Editing the grid, or changing how it plays, stops what is sounding rather than letting the
  // previous version play on under the new one. The click and the count-in aren't among them: one
  // is switched live and the other is only heard on Play.
  useEffect(() => {
    teardown();
    setIsPlaying(false);
    setIsLoading(false);
  }, [run, loop, drumGroove, teardown]);

  // Ramped rather than set: a level jump on a sounding note is a click of its own
  useEffect(() => {
    if (gainRef.current) gainRef.current.gain.rampTo(chordsLevel, 0.05);
  }, [chordsLevel]);

  useEffect(() => {
    if (drumGainRef.current) drumGainRef.current.gain.rampTo(drumVolume, 0.05);
  }, [drumVolume]);

  // Switched off mid-phrase, the phrase stops too
  useEffect(() => {
    if (!speakCallouts) cancelSpeech();
  }, [speakCallouts]);

  useEffect(() => {
    if (melodyRef.current) melodyRef.current.gain.gain.rampTo(melodyVolume, 0.05);
  }, [melodyVolume]);

  useEffect(() => {
    if (bassRef.current) bassRef.current.gain.gain.rampTo(bassVolume, 0.05);
  }, [bassVolume]);

  useEffect(() => {
    if (padRef.current) padRef.current.gain.gain.rampTo(padVolume, 0.05);
  }, [padVolume]);

  useEffect(() => {
    Object.entries(bandRef.current).forEach(([id, { gain }]) => gain.gain.rampTo(bandVolumes[id] ?? 1, 0.05));
  }, [bandVolumes]);

  useEffect(() => {
    Object.entries(sendsRef.current).forEach(([id, sends]) => rampSends(sends, id, effects));
  }, [effects]);

  // A play still loading is let go of too: it sees the teardown and stops where it is
  const stop = useCallback(() => {
    teardown();
    setIsPlaying(false);
    setIsLoading(false);
  }, [teardown]);

  const play = useCallback(async () => {
    const notes = flattenMidiNotes(run?.midiJson);
    const segments = run?.segments || [];
    const [first] = segments;

    if (notes.length === 0 || !first) return;

    const {
      duration,
      melodyNotes,
      melodyEnd,
      bassNotes = [],
      padNotes = [],
      padLevels = [],
      bandNotes = {},
      bandLevels = {},
    } = run;

    // Before anything is awaited, while this is still the tap: iOS only speaks once a tap has started it
    if (callouts) primeSpeech();

    setIsLoading(true);
    setError('');

    // Checked after every await: a teardown since means this play was stopped or played over, and
    // what it made while waiting is let go of rather than played under the next one
    let playId = playIdRef.current;
    const isStale = () => playIdRef.current !== playId;

    try {
      const Tone = toneRef.current || (await loadTone());
      toneRef.current = Tone;

      if (!(await resumeContext(Tone))) {
        setError(i18n.t('player.audioBlocked'));

        return;
      }

      if (isStale()) return;

      teardown();
      playId = playIdRef.current;

      const master = createMaster(Tone);
      masterRef.current = master;

      const analyser = new Tone.Analyser('fft', 256);
      master.connect(analyser);
      analyserRef.current = analyser;

      // The voice plays into a gain of its own rather than straight into the master limiter, so
      // the chords can be balanced against the click. It sits before the limiter, which means
      // turning them down gives the limiter less to catch rather than changing what it hears.
      const gain = new Tone.Gain(instrumentVolumeRef.current).connect(master);
      gainRef.current = gain;

      const voices = await createVoices(Tone, notes, gain);

      if (isStale()) {
        voices.forEach(disposeVoice);

        return;
      }

      voicesRef.current = [...voices.values()];

      const transport = Tone.getTransport();

      // Note times are absolute seconds but Part.add converts them to ticks as each event is
      // added, so the tempo has to be settled before anything is built. The transport is global
      // and holds whatever the last clip left on it. A part further into a run at another tempo
      // needs nothing of it: its seconds are already its own.
      transport.bpm.value = run.bpm;

      // After the tempo, which the delay's dotted eighth is timed from
      const buses = await createSendBuses(Tone, master, effectsRef.current);

      if (isStale()) {
        buses.nodes.forEach((node) => node.dispose());

        return;
      }

      busesRef.current = buses;

      const addSends = (id, source) => {
        const sends = connectSends(Tone, source, buses, id, effectsRef.current);

        if (sends) sendsRef.current[id] = sends;
      };

      addSends('piano', gain);

      // Where in the grid to begin. This is a Part offset, not a transport one, which is what keeps
      // the two other things about the start honest: the count-in is scheduled against the
      // transport so it still plays in full, and the loop is the Part's own so it still wraps to
      // loopStart — the top of the chart — rather than back to the bar playing started from.
      const startAt = Math.min(Math.max(startBarRef.current || 0, 0), Math.max(first.bars - 1, 0)) * first.secondsPerBar;

      // A chart that comes in during the count-in has its first bar sounding under the count
      const overlaps = countIn && run.countInEntry && startAt === 0;
      const offset = countIn && !overlaps ? first.secondsPerBar : 0;

      const chordPart = new Tone.Part(
        (time, note) => {
          voices.get(note.instrumentId).voice.triggerAttackRelease(
            Tone.Frequency(note.midi, 'midi').toFrequency(),
            // What it rings for, which is not how long the key was held: a pedalled note carries a
            // `sustain` past its own length. The same reading useMidiPlayer makes of an uploaded
            // clip, so a pedalled rhythm and a pedalled recording sound alike.
            getRingTime(note),
            time,
            curveVelocity(note.velocity),
          );
        },
        notes.map((note) => [note.time, note]),
      );

      // The parts loop themselves rather than the transport doing it, so the count-in can sit
      // outside the loop: it plays once, ahead of a grid that then repeats on its own.
      chordPart.loop = loop;
      chordPart.loopStart = 0;
      chordPart.loopEnd = duration;
      chordPart.start(offset, startAt);
      partsRef.current.push(chordPart);

      // The drums are a Part of their own beside the chords, looping and offset the same way, so
      // the two stay one performance. Laid out from each segment's first bar line rather than from
      // each chord — see drumGrooves.js — and kept out of `notes` entirely: they are not keys, and
      // the keyboard lighting up for a kick would be the preview inventing notes the chart never
      // wrote. A segment can name a groove of its own: a part played through in its own style.
      const grooveOf = (segment) => (segment.drumGroove !== undefined ? segment.drumGroove : drumGroove);

      if (segments.some(grooveOf)) {
        // Each at its part's intensity, with fills where the level changes
        const laid = getRunDrumHits(
          segments.map((segment) => ({ ...segment, drumGroove: grooveOf(segment) })),
          beatsPerBar,
        );
        const hits = segments.flatMap((segment, index) =>
          laid[index].map((hit) => ({
            ...hit,
            time: segment.start + hit.at * segment.secondsPerBeat,
            holdSeconds: hit.hold ? hit.hold * segment.secondsPerBeat : null,
          })),
        );

        if (hits.length > 0) {
          const drumGain = new Tone.Gain(drumVolumeRef.current).connect(master);
          drumGainRef.current = drumGain;
          addSends('drums', drumGain);

          const kit = createDrumKit(Tone, drumGain, [...new Set(hits.map((hit) => hit.piece))]);
          drumKitRef.current = kit;

          // Under the count-in the band comes in with the first chord, not on the count's downbeat
          const entryAt = first.chordTimes[0] ?? 0;
          let beforeEntry = overlaps ? hits.filter((hit) => hit.time < entryAt - 1e-6).length : 0;

          const drumPart = new Tone.Part(
            (time, hit) => {
              if (beforeEntry > 0) {
                beforeEntry -= 1;

                return;
              }

              kit.trigger(hit.piece, time, hit.level, hit.holdSeconds);
            },
            hits.map((hit) => [hit.time, hit]),
          );

          drumPart.loop = loop;
          drumPart.loopStart = 0;
          drumPart.loopEnd = duration;
          drumPart.start(offset, startAt);
          partsRef.current.push(drumPart);
        }
      }

      // The melody is a Part beside the chords, offset and looped with them so it lands on the same
      // bar lines. The keyboard stays on the chords: it is the chart's keyboard.
      const fellBack = [...voices.values()];

      if (melodyNotes.length > 0) {
        const melodyGain = new Tone.Gain(melodyVolumeRef.current).connect(master);
        addSends('melody', melodyGain);
        const melodyVoices = await createVoices(Tone, melodyNotes, melodyGain);

        if (isStale()) {
          melodyVoices.forEach(disposeVoice);
          melodyGain.dispose();

          return;
        }

        melodyRef.current = { voices: [...melodyVoices.values()], gain: melodyGain };
        fellBack.push(...melodyVoices.values());

        const playMelodyNote = (time, note) => {
          melodyVoices.get(note.instrumentId).voice.triggerAttackRelease(
            Tone.Frequency(note.midi, 'midi').toFrequency(),
            getRingTime(note),
            time,
            curveVelocity(note.velocity),
          );
        };

        // The first part's pickup comes before 0: heard once over the count-in, and over the run's
        // tail on each wrap of a loop, where it leads back into the top
        const pickup = melodyNotes.filter((note) => note.time < 0);
        const wrapped = loop ? pickup.map((note) => ({ ...note, time: note.time + duration })) : [];

        const melodyPart = new Tone.Part(
          playMelodyNote,
          [...melodyNotes.filter((note) => note.time >= 0), ...wrapped].map((note) => [note.time, note]),
        );

        melodyPart.loop = loop;
        melodyPart.loopStart = 0;
        melodyPart.loopEnd = duration;
        melodyPart.start(offset, startAt);
        partsRef.current.push(melodyPart);

        // Only what fits in the count-in, and only from the top: a later start has no pickup to lead in
        const overCountIn = startAt === 0 ? pickup.filter((note) => offset + note.time >= 0) : [];

        if (overCountIn.length > 0) {
          const pickupPart = new Tone.Part(
            playMelodyNote,
            overCountIn.map((note) => [offset + note.time, note]),
          );

          pickupPart.start(0);
          partsRef.current.push(pickupPart);
        }
      }

      // A layer beside the chords that only the run's sound has, like the bass player and the pad: a Part
      // of its own into its own gain, off the keyboard. Laid out with the chords, so it needs nothing
      // held back under the count-in.
      // `levels`, where given, ramps a second gain after the mixer's through the run: the pad's
      // padLevels, as it is kept on quieter under the quieter parts, or the violin's swells and fades, which
      // ramp in dB where a level says so. `sendsAs` names its reverb and delay sends.
      const playLayer = async (layerNotes, level, { levels = [], sendsAs = null } = {}) => {
        const atStart = levels.filter(({ time }) => time <= startAt + 1e-6).pop();
        const shaping = levels.length > 0 ? new Tone.Gain(atStart?.value ?? 1).connect(master) : null;
        const layerGain = new Tone.Gain(level).connect(shaping || master);

        // From the end of the layer's chain, so the pad's shaping is heard in its reverb too
        if (sendsAs) addSends(sendsAs, shaping || layerGain);
        const layerVoices = await createVoices(Tone, layerNotes, layerGain);

        // Null, which the caller doesn't keep: it checks isStale before taking a layer
        if (isStale()) {
          layerVoices.forEach(disposeVoice);
          layerGain.dispose();
          if (shaping) shaping.dispose();

          return null;
        }

        if (shaping) {
          const levelPart = new Tone.Part((time, { value, ramp, curve }) => {
            shaping.gain.cancelScheduledValues(time);
            if (ramp > 0 && curve === EXPONENTIAL) shaping.gain.exponentialRampTo(value, ramp, time);
            else if (ramp > 0) shaping.gain.rampTo(value, ramp, time);
            else shaping.gain.setValueAtTime(value, time);
          }, levels.map((change) => [change.time, change]));

          levelPart.loop = loop;
          levelPart.loopStart = 0;
          levelPart.loopEnd = duration;
          levelPart.start(offset, startAt);
          partsRef.current.push(levelPart);
        }

        fellBack.push(...layerVoices.values());

        const layerPart = new Tone.Part(
          (time, note) => {
            layerVoices.get(note.instrumentId).voice.triggerAttackRelease(
              Tone.Frequency(note.midi, 'midi').toFrequency(),
              getRingTime(note),
              time,
              curveVelocity(note.velocity),
            );
          },
          layerNotes.map((note) => [note.time, note]),
        );

        layerPart.loop = loop;
        layerPart.loopStart = 0;
        layerPart.loopEnd = duration;
        layerPart.start(offset, startAt);
        partsRef.current.push(layerPart);

        return { voices: [...layerVoices.values()], gain: layerGain, shaping };
      };

      // Each checked before it's kept, so a stale play can't write over the refs of the one after it
      if (bassNotes.length > 0) {
        const bass = await playLayer(bassNotes, bassVolumeRef.current);
        if (isStale()) return;
        bassRef.current = bass;
      }

      if (padNotes.length > 0) {
        const pad = await playLayer(padNotes, padVolumeRef.current, { levels: padLevels, sendsAs: 'pad' });
        if (isStale()) return;
        padRef.current = pad;
      }

      for (const [id, layerNotes] of Object.entries(bandNotes)) {
        if (layerNotes.length > 0) {
          const layer = await playLayer(layerNotes, bandVolumesRef.current[id] ?? 1, {
            levels: bandLevels[id] || [],
            sendsAs: id,
          });
          if (isStale()) return;
          bandRef.current[id] = layer;
        }
      }

      const fallback = fellBack.find((voice) => voice.fellBackFrom);

      if (fallback) setError(i18n.t('player.fellBack', { from: fallback.fellBackFrom, to: getInstrumentName(fallback.preset) }));

      // Each segment's own beats, so a part at another tempo is clicked at its own
      const beats = segments.flatMap((segment) =>
        Array.from({ length: segment.bars * beatsPerBar }, (_, i) => [
          segment.start + i * segment.secondsPerBeat,
          i % beatsPerBar === 0,
        ]),
      );

      // Under the count-in, the first bar's clicks are the count-in's own; a loop back to it clicks it
      let underCountIn = overlaps ? beatsPerBar : 0;

      const clickPart = new Tone.Part((time, isDownbeat) => {
        if (underCountIn > 0) {
          underCountIn -= 1;

          return;
        }

        if (metronomeRef.current) scheduleMetronomeClick(Tone, time, isDownbeat, volumeRef.current);
      }, beats);

      clickPart.loop = loop;
      clickPart.loopStart = 0;
      clickPart.loopEnd = duration;
      clickPart.start(offset, startAt);
      partsRef.current.push(clickPart);

      // Heard even with the click off: a silent bar before the chords is just a wait
      if (countIn) {
        const countInPart = new Tone.Part(
          (time, isDownbeat) => scheduleMetronomeClick(Tone, time, isDownbeat, volumeRef.current),
          Array.from({ length: beatsPerBar }, (_, i) => [i * first.secondsPerBeat, i === 0]),
        );

        countInPart.start(0);
        partsRef.current.push(countInPart);
      }

      // Each part is called on the downbeat of the bar before it: the last bar of the part ahead of
      // it — or the bar before its melody's pickup — and for the first, the count-in, or with no
      // count-in, as it starts. Spoken from Draw, which runs on the frame the audio clock reaches
      // that time, since speech isn't Web Audio.
      if (callouts) {
        const say = (part) => (time, text) => {
          Tone.getDraw().schedule(() => {
            if (speakCalloutsRef.current && partsRef.current.includes(part)) speak(text, calloutLang, calloutVolumeRef.current);
          }, time);
        };

        if (callouts[0]) {
          const firstPart = new Tone.Part();
          firstPart.callback = say(firstPart);
          firstPart.add(0, callouts[0]);
          firstPart.start(0);
          partsRef.current.push(firstPart);
        }

        const later = segments
          .map((segment, i) => [getCalloutTime(segments, i), callouts[i]])
          .filter(([, text], i) => i > 0 && text);

        if (later.length > 0) {
          const calloutPart = new Tone.Part();
          calloutPart.callback = say(calloutPart);
          later.forEach(([time, text]) => calloutPart.add(time, text));
          calloutPart.start(offset, startAt);
          partsRef.current.push(calloutPart);
        }
      }

      transport.position = 0;
      transport.start();

      const segmentStarts = segments.map((segment) => segment.start);

      const trackPosition = () => {
        // Elapsed is time since the chords came in; the grid was already startAt seconds deep then
        const played = startAt + (transport.seconds - offset);
        // Looping is the parts' own, so the transport clock keeps running past the end of the
        // grid — the position within it has to be wrapped back by hand
        const position = transport.seconds < offset ? null : loop ? played % duration : Math.min(played, duration);

        const sounding = position === null ? NO_ACTIVE_NOTES : getSoundingNotes(notes, position);

        // Which click of the count-in is sounding — the bar before the chart, which is exactly the
        // stretch `position` has nothing to say about. Quantised to the beat like the chord below,
        // so a view following it re-renders on the click rather than every frame.
        // A chart come in during the count is already playing under it, so the count is its first bar.
        const counting = overlaps ? transport.seconds < first.secondsPerBar : position === null;
        const clickBeat = counting
          ? Math.min(Math.floor(transport.seconds / first.secondsPerBeat) + 1, beatsPerBar)
          : NO_BEAT;

        // The segment playing, and how far into it — everything below is read in its own bars
        const index = position === null ? NO_SEGMENT : strikeAt(segmentStarts, position);
        const segment = segments[index];
        const local = segment ? position - segment.start : 0;

        // The same count carried on into the chart, until it has played through
        let barBeat = clickBeat;
        if (segment) {
          barBeat = !loop && played >= duration ? NO_BEAT : (Math.floor(local / segment.secondsPerBeat) % beatsPerBar) + 1;
        }

        // Quantised to the chord, so this re-renders where the chart changes rather than every frame
        const struck = segment ? strikeAt(segment.chordTimes, local) : NO_CHORD;
        // Nothing is ringing through a rest, though the chord before it is still the last struck
        const chord = struck !== NO_CHORD && local >= (segment.chordEnds[struck] ?? Infinity) ? NO_CHORD : struck;

        // As one render: outside an event handler React 17 renders each of these on its own, and at a
        // part line a view would get the new part with the last part's chord still lit in it
        batchedUpdates(() => {
          setCountInBeat((previous) => (previous === clickBeat ? previous : clickBeat));
          setBeat((previous) => (previous === barBeat ? previous : barBeat));
          setActiveNotes((previous) => (haveSameNotes(previous, sounding) ? previous : sounding));
          setCurrentSegment(index);
          setCurrentBar(segment ? Math.min(Math.floor(local / segment.secondsPerBar), segment.bars - 1) : NO_BAR);
          setCurrentChord((previous) => (previous === chord ? previous : chord));
        });

        frameRef.current = requestAnimationFrame(trackPosition);
      };

      trackPosition();

      setIsPlaying(true);

      // A Tone.Part has no "finished" event, so the end is timed from the grid's own length. A
      // looping grid has no end — it runs until the listener stops it.
      if (!loop) {
        const releaseTail = Math.max(...fellBack.map(({ preset }) => preset.releaseTail || RELEASE_TAIL));

        endTimeoutRef.current = setTimeout(
          () => {
            teardown();
            setIsPlaying(false);
          },
          (offset + (Math.max(duration, melodyEnd) - startAt) + releaseTail) * 1000,
        );
      }
    } catch (err) {
      // A stale play failing on nodes already torn down is no error, and its teardown would stop the next one
      if (isStale()) return;

      // Audio failures are otherwise indistinguishable from silence
      console.error('Chord bars playback failed', err);
      setError(i18n.t('player.chordsFailed'));
      teardown();
      setIsPlaying(false);
    } finally {
      // Whatever stopped a stale play cleared this already, and the play after it may still be loading
      if (!isStale()) setIsLoading(false);
    }
  }, [run, beatsPerBar, loop, countIn, drumGroove, callouts, calloutLang, teardown]);

  // The master's spectrum in dB, low to high, or null while nothing is set up to play
  const getSpectrum = useCallback(() => analyserRef.current?.getValue() ?? null, []);

  return {
    isLoading,
    isPlaying,
    error,
    play,
    stop,
    activeNotes,
    currentSegment,
    currentBar,
    currentChord,
    countInBeat,
    beat,
    getSpectrum,
    // The share taken off the chords right now, for the slider to show
    chordsCut: isPlaying && isUnderMelody ? 1 - chordsUnderMelody : 0,
  };
};

export default useChordGridPlayer;
